import { Fragment, useState } from 'react';
import { Tooltip } from 'antd';
import {
	ArrowDownOutlined,
	ArrowRightOutlined,
	ArrowUpOutlined,
	CheckOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import ConditionEditor from './ConditionEditor';
import {
	addCondition,
	removeCondition,
	setRuleSpec,
	updateCondition,
} from 'src/_shared/services/job-sync/serde';
import { operatorLabel, valueKind } from 'src/_shared/services/job-sync/operators';

// Read-only mono chip for a field or value in the sentence view. The text lives in a block child
// because `text-overflow` is inert on the chip itself — a flex container blockifies its text into an
// anonymous item, so ellipsis only applies one level in.
export function ReadChip({ value, placeholder, accent, className }) {
	const empty = value == null || value === '';
	const text = empty ? placeholder : String(value);
	return (
		<span
			className={`js-chip${empty ? ' is-empty' : ''}${accent ? ' js-chip-accent' : ''}${className ? ` ${className}` : ''}`}
			title={empty ? undefined : text}
		>
			<span className="js-chip-txt">{text}</span>
		</span>
	);
}

// Natural-language operator words for the read view ("is any of" reads better than "in").
function operatorWords(operator) {
	if (operator === 'in') return 'is any of';
	if (operator === 'not_in') return 'is none of';
	return operatorLabel(operator);
}

// The value chips of an `is any of` / `is none of` condition. Past `truncateAt` they collapse behind a
// "+N more" pill so a six-value rule keeps the same one-line rhythm as its neighbours; clicking the pill
// expands the row in place. The pill is a SIBLING of the chip group, not a child: the group is the only
// shrinkable region (it clips under width pressure), and the pill must never be clipped with it.
function ConditionValues({ values, truncateAt }) {
	const [open, setOpen] = useState(false);
	if (values.length === 0) return <ReadChip value="" placeholder="values" />;

	const shown = open ? values : values.slice(0, truncateAt);
	const overflow = values.length - shown.length;

	return (
		<>
			<span className={`js-vals${open ? ' is-open' : ''}`}>
				{shown.map((v, i) => (
					// eslint-disable-next-line react/no-array-index-key
					<ReadChip key={i} value={v} placeholder="value" />
				))}
			</span>
			{(overflow > 0 || open) && (
				<button type="button" className="js-morepill" aria-expanded={open} onClick={() => setOpen(!open)}>
					{open ? 'show less' : `+${overflow} more`}
				</button>
			)}
		</>
	);
}

// One condition as read-only sentence text: field chip · operator words · value chip(s).
function ConditionText({ condition, truncateAt }) {
	const kind = valueKind(condition.operator);
	const values = Array.isArray(condition.value) ? condition.value : [];
	return (
		<span className="js-cond">
			<ReadChip value={condition.field} placeholder="field" className="js-chip-field" />
			<span className="js-kw">{operatorWords(condition.operator)}</span>
			{kind === 'array' && <ConditionValues values={values} truncateAt={truncateAt} />}
			{kind === 'scalar' && (
				<ReadChip value={condition.value} placeholder="value" className="js-chip-scalar" />
			)}
		</span>
	);
}

// One rule row. At rest it renders as a read-only plain-English sentence
// (`① If <field> <operator> <value> → <result>`, per the design); the pencil switches the row into
// the inline editor (condition controls + result control). Reorder/delete live in the hover tail.
//
// The read view is a four-column grid — badge · condition · outcome rail · actions — so every outcome
// shares one left edge however long its condition runs, and the row border spans the card's full width
// with the hover-revealed actions inside it. The edit branch stays a flat wrapping flex sentence (its
// antd controls need to wrap) with the same actions block hugging the right edge.
export default function RuleRow({
	rule,
	index,
	total,
	fieldOptions,
	outLabel,
	renderResult,
	renderResultRead,
	truncateAt = 1,
	defaultEditing = false,
	onChange,
	onRemove,
	onMoveUp,
	onMoveDown,
}) {
	const [editing, setEditing] = useState(defaultEditing);
	const conds = rule.when || [];
	const onSpecChange = (spec) => onChange(setRuleSpec(rule, spec));

	return (
		<div className={`js-rule${editing ? ' is-editing' : ''}`}>
			<div className="js-rule-body">
				<span className="js-sent-no" aria-hidden>
					{index + 1}
				</span>

				{editing ? (
					<>
						<span className="js-kw">If</span>
						{conds.map((c, i) => (
							// eslint-disable-next-line react/no-array-index-key
							<Fragment key={i}>
								{i > 0 && <span className="js-kw">and</span>}
								<ConditionEditor
									condition={c}
									fieldOptions={fieldOptions}
									canRemove={conds.length > 1}
									onChange={(next) => onChange(updateCondition(rule, i, next))}
									onRemove={() => onChange(removeCondition(rule, i))}
								/>
							</Fragment>
						))}
						<button type="button" className="js-linkbtn js-addcond" onClick={() => onChange(addCondition(rule))}>
							<PlusOutlined /> and
						</button>

						<span className="js-arrow" aria-hidden>
							<ArrowRightOutlined />
						</span>
						<span className="js-kw">{outLabel}</span>
						<div className="js-token js-result js-token-val">{renderResult(rule.set, onSpecChange)}</div>
					</>
				) : (
					<>
						<div className={`js-cond-wrap${conds.length > 1 ? ' is-multi' : ''}`}>
							<span className="js-kw">If</span>
							{conds.map((c, i) => (
								// eslint-disable-next-line react/no-array-index-key
								<Fragment key={i}>
									{i > 0 && <span className="js-kw">and</span>}
									<ConditionText condition={c} truncateAt={truncateAt} />
								</Fragment>
							))}
						</div>
						<div className="js-rule-rail">
							<span className="js-arrow" aria-hidden>
								<ArrowRightOutlined />
							</span>
							<span className="js-res-text">{renderResultRead(rule.set)}</span>
						</div>
					</>
				)}

				<div className="js-rule-tail">
					<Tooltip title={editing ? 'Done' : 'Edit rule'}>
						<button
							type="button"
							className={`js-iconbtn${editing ? ' is-on' : ''}`}
							onClick={() => setEditing(!editing)}
							aria-label={editing ? 'Done editing rule' : 'Edit rule'}
						>
							{editing ? <CheckOutlined /> : <EditOutlined />}
						</button>
					</Tooltip>
					<div className="js-reorder">
						<button
							type="button"
							className="js-iconbtn"
							disabled={index === 0}
							onClick={onMoveUp}
							aria-label="Move rule up"
						>
							<ArrowUpOutlined />
						</button>
						<button
							type="button"
							className="js-iconbtn"
							disabled={index === total - 1}
							onClick={onMoveDown}
							aria-label="Move rule down"
						>
							<ArrowDownOutlined />
						</button>
					</div>
					<Tooltip title="Remove rule">
						<button type="button" className="js-iconbtn js-danger" onClick={onRemove} aria-label="Remove rule">
							<DeleteOutlined />
						</button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
}
